import { useState, useEffect, useMemo, useCallback } from "react";
import { useRealStudents } from "@/hooks/data/useRealStudents";
import { useRealAttendance } from "@/hooks/data/useRealAttendance";
import { useRealSchoolSettings } from "@/hooks/data/useRealSchoolSettings";
import { useStageOrdering } from "@/hooks/settings/useStageOrdering";
import { StudentWithAttendance } from "@/types/attendance";
import {
  getAvailableGrades,
  getAvailableSections,
} from "@/utils/attendanceFilters";
import { filterAndSortStudents } from "@/utils/studentSorting";
import {
  processStudentsWithAttendance,
  getStageDisplayName,
} from "@/utils/attendanceData";
import { dateSplitter } from "@/utils/formatting/deteSplitter";
export const useAttendanceState = () => {
  const [selectedStage, setSelectedStage] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedDate, setSelectedDate] = useState(dateSplitter(new Date()));
  // const [selectedPeriod, setSelectedPeriod] = useState("1");
  // const [studentsWithAttendance, setStudentsWithAttendance] = useState<
  //   StudentWithAttendance[]
  // >([]);
  const [attendanceChanges, setAttendanceChanges] = useState<{
    [studentId: string]:
      | "present"
      | "absent"
      | "late"
      | "absent_with_reason"
      | undefined;
  }>({});
  // const [isEditMode, setIsEditMode] = useState(false);
  // const [hasExistingAttendance, setHasExistingAttendance] = useState(false);
  // const [isLoading, setIsLoading] = useState(false);

  const { students: realStudents } = useRealStudents();

  const { settings, loading: settingsLoading } = useRealSchoolSettings();

  // تحويل بسيط للطلاب مع تحسين الأداء
  const students = useMemo(() => {
    if (!realStudents || realStudents.length === 0) return [];

    return realStudents.map((student) => ({
      id: student.id,
      name: student.name,
      grade: student.grade,
      section: student.section,
      studentId: student.student_id,
    }));
  }, [realStudents]);

  // الحصول على المراحل المفعلة مع تحسين الأداء
  const allStages = useMemo(() => {
    if (!settings?.stages) return [];
    return settings.stages;
  }, [settings?.stages]);

  const enabledStages = useMemo(() => {
    if (!allStages) return [];
    return allStages.filter((stage: any) => stage && stage.enabled === true);
  }, [allStages]);

  const orderedStages = useStageOrdering(enabledStages);
  const hasMultipleStages = orderedStages.length > 1;

  // حساب الصفوف والشعب المتاحة مع منع التحديثات غير الضرورية
  // const availableGrades = useMemo(() => {
  //   if (!students || students.length === 0) return [];

  //   try {
  //     return getAvailableGrades(
  //       students,
  //       enabledStages,
  //       hasMultipleStages,
  //       selectedStage,
  //       orderedStages
  //     );
  //   } catch (error) {
  //     console.error("Error calculating available grades:", error);
  //     return [];
  //   }
  // }, [
  //   students,
  //   enabledStages,
  //   hasMultipleStages,
  //   selectedStage,
  //   orderedStages,
  // ]);

  // const availableSections = useMemo(() => {
  //   if (!selectedGrade || !students || students.length === 0) return [];

  //   try {
  //     return getAvailableSections(
  //       selectedGrade,
  //       students,
  //       enabledStages,
  //       hasMultipleStages,
  //       selectedStage,
  //       orderedStages
  //     );
  //   } catch (error) {
  //     console.error("Error calculating available sections:", error);
  //     return [];
  //   }
  // }, [
  //   selectedGrade,
  //   students,
  //   enabledStages,
  //   hasMultipleStages,
  //   selectedStage,
  //   orderedStages,
  // ]);

  // دوال تغيير الحالة مع إعادة تعيين البيانات المرتبطة
  // const handleStageChange = useCallback(
  //   (stage: string) => {
  //     // console.log("Stage change requested:", stage, "current:", selectedStage);
  //     setSelectedStage(stage);
  //     setSelectedGrade("");
  //     setSelectedSection("");
  //     setStudentsWithAttendance([]);
  //     setHasExistingAttendance(false);
  //     setIsEditMode(false);
  //     setAttendanceChanges({});
  //   },
  //   []
  // );

  // const handleGradeChange = useCallback(
  //   (grade: string) => {
  //     // console.log("Grade change requested:", grade, "current:", selectedGrade);
  //     setSelectedGrade(grade);
  //     setSelectedSection("");
  //     setStudentsWithAttendance([]);
  //     setHasExistingAttendance(false);
  //     setIsEditMode(false);
  //     setAttendanceChanges({});
  //   },
  //   []
  // );

  // const handleSectionChange = useCallback(
  //   (section: string) => {
  //     setSelectedSection(section);
  //     setStudentsWithAttendance([]);
  //     setHasExistingAttendance(false);
  //     setIsEditMode(false);
  //     setAttendanceChanges({});
  //   },
  //   []
  // );

  // تحميل بيانات الحضور مع تحسين الأداء ومنع التحديثات المتكررة
  useEffect(() => {
    if (
      !selectedGrade ||
      !selectedSection ||
      !students ||
      students.length === 0
    ) {
      // setStudentsWithAttendance([]);
      // setHasExistingAttendance(false);
      // setIsEditMode(false);
      // setAttendanceChanges({});
      return;
    }

    let isCancelled = false;

    const loadData = async () => {
      if (isCancelled) return;

      // setIsLoading(true);

      try {
        // جلب بيانات الحضور
        // await fetchAttendanceByDate(selectedDate);

        if (isCancelled) return;

        // تصفية الطلاب
        const filteredStudents = filterAndSortStudents(
          students,
          selectedGrade,
          selectedSection,
          selectedStage,
          // hasMultipleStages,
          orderedStages
        );

        if (isCancelled) return;

        if (filteredStudents.length === 0) {
          // setStudentsWithAttendance([]);
          // setHasExistingAttendance(false);
          // setIsEditMode(false);
          setAttendanceChanges({});
          return;
        }

        // معالجة بيانات الحضور
        // const { studentsWithAttendanceData, hasExisting } =
        //   processStudentsWithAttendance(
        //     filteredStudents,
        //     (studentId: string) => {
        //       const record = getStudentAttendance(studentId, selectedDate);
        //       return record ? [record] : [];
        //     },
        //     selectedDate,
        //     {},
        //     undefined
        //   );

        if (isCancelled) return;

        // setStudentsWithAttendance(studentsWithAttendanceData);
        // setHasExistingAttendance(hasExisting);
        // setIsEditMode(!hasExisting);
        setAttendanceChanges({});
      } catch (error) {
        console.error("Error loading attendance:", error);
        if (!isCancelled) {
          // setStudentsWithAttendance([]);
          // setHasExistingAttendance(false);
          // setIsEditMode(false);
          setAttendanceChanges({});
        }
      } finally {
        if (!isCancelled) {
          // setIsLoading(false);
        }
      }
    };

    // تأخير بسيط لمنع الاستدعاءات المتكررة
    const timeoutId = setTimeout(loadData, 150);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    selectedGrade,
    selectedSection,
    selectedDate,
    students,
    // getStudentAttendance,
    // fetchAttendanceByDate,
    selectedStage,
    hasMultipleStages,
    orderedStages,
  ]);

  // دالة تسجيل الحضور المحسنة مع منع التحديثات المتكررة
  // const optimizedRecordAttendance = useCallback(
  //   async (
  //     studentId: string,
  //     date: string,
  //     status: "present" | "absent" | "late" | "excused" | "absent_with_reason",
  //     period?: number
  //   ) => {
  //     if (!studentId || !date || !status) {
  //       throw new Error("Missing required parameters");
  //     }

  //     return await recordAttendance({ studentId, date, status, period });
  //   },
  //   [recordAttendance]
  // );

  return {
    // State
    // selectedStage,
    // selectedGrade,
    // selectedSection,
    // selectedDate,
    // selectedPeriod,
    // studentsWithAttendance,
    // attendanceChanges,
    // isEditMode,
    // hasExistingAttendance,
    enabledStages: orderedStages,
    hasMultipleStages,
    // availableGrades,
    // availableSections,
    // settings,
    // students,
    // recordAttendance: optimizedRecordAttendance,
    // loading: settingsLoading || isLoading,
    // processingAttendance: false,

    // // Actions
    // setSelectedStage: handleStageChange,
    // setSelectedGrade: handleGradeChange,
    // setSelectedSection: handleSectionChange,
    // setSelectedDate,
    // setSelectedPeriod,
    // setAttendanceChanges,
    // setIsEditMode,
    // getStageDisplayName,
  };
};
